import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ServiceCode } from '../../../../common/enums/serviceCode';
import type { IPatient } from '../../../../common/interfaces/patient';
import type { IReduxState } from '../../../configureStore';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import { ReportStatus } from '../../../../common/enums/reportStatus';
import { ServiceType } from '../../../../common/enums/serviceType';
import { getJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { EvaluationDiagnosticReport } from '../../pages/evaluation/EvaluationDiagnosticReport';
import { Button } from '../common';
import { ConvertToConsultOrReferralModal } from '../ConvertToConsultOrReferralModal';
import { confirmDiagnosticReport, confirmDiagnosticReportToast } from './ConfirmDiagnosticReportButton';

interface IConvertToConsultButtonProps {
    acceptDiagnosticReport?: () => Promise<void>;
    diagnosticReport: IDiagnosticReportGetOne;
    isPreviousRequest?: boolean;
    onConvertToReferral?: (referralServiceId: string, requestId: string) => Promise<void>;
    patient: IPatient;
    request: IRequestGetOne;
    serviceCode: ServiceCode;
    serviceId: string;
    serviceType: ServiceType;
}

interface IService {
    code: ServiceCode;
    id: string;
    name: string;
    serviceType: ServiceType;
}

interface ISubsequentService {
    succeedingService: IService;
}

export function ConvertToReferralButton(props: IConvertToConsultButtonProps) {
    const [services, setServices] = useState<IService[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [showEvaluation, setShowEvaluation] = useState(false);
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);

    async function loadService() {
        const result = await getJson(
            `/api/subsequentServices?serviceId=${props.serviceId}&isActive=true&useLoggedInEmployee=true&succeedingServiceTypes=${ServiceType.referral},${ServiceType.referralSmall}`
        );

        if (result.json.items.length) {
            setServices(result.json.items.map((item: ISubsequentService) => item.succeedingService));
        }
    }

    useEffect(() => {
        if (props.serviceType === ServiceType.examination || props.serviceType === ServiceType.consultation) {
            loadService();
        }
    }, [props.serviceId]);

    if (services.length === 0 || props.isPreviousRequest || !props.onConvertToReferral) {
        return null;
    }
    if (props.request.authorization && !props.request.authorization.canAddReferral) {
        return null;
    }

    if (showModal) {
        return (
            <ConvertToConsultOrReferralModal
                closeModal={() => setShowModal(false)}
                isReferral
                onConvertToConsultorReferral={async (serviceId) => {
                    setShowModal(false);
                    await convertToReferral(serviceId);
                }}
                services={services}
            />
        );
    }

    return (
        <>
            <Button
                automation="convertToReferral"
                color="secondary"
                large
                onClick={async () => await onClick()}
                outlined
            >
                {translateKey('request.statusAction_convertToReferral')}
            </Button>

            <EvaluationDiagnosticReport
                isOpen={showEvaluation}
                onClose={async () => {
                    setShowEvaluation(false);
                    if (services.length > 1) {
                        setShowModal(true);
                    } else {
                        await convertToReferral(services[0].id);
                    }
                }}
                requestId={props.request.id}
            />
        </>
    );

    async function convertToReferral(serviceId: string) {
        if (
            props.acceptDiagnosticReport &&
            props.diagnosticReport.authorization &&
            props.diagnosticReport.authorization.canUpdateDiagnosticReportToClosed
        ) {
            await confirmDiagnosticReport(props.diagnosticReport.id, true);
            await confirmDiagnosticReportToast(props.serviceCode, props.request, props.patient, selectedEmploymentId);
            await props.acceptDiagnosticReport();
        }
        if (props.onConvertToReferral) {
            await props.onConvertToReferral(serviceId, props.request.id);
        }
    }

    async function onClick() {
        if (
            props.request.serviceType === ServiceType.consultation &&
            props.diagnosticReport.reviewStatus === ReportStatus.open
        ) {
            setShowEvaluation(true);
        } else {
            if (services.length > 1) {
                setShowModal(true);
            } else {
                await convertToReferral(services[0].id);
            }
        }
    }
}
